/**
 * @fileoverview Australian business-day arithmetic with jurisdiction-aware public holidays
 * @module lib/businessDays
 *
 * Shared platform utility (MOR-830 follow-up). "Business day" (Corporations
 * Act s9) excludes Saturdays, Sundays and public holidays *in the place
 * concerned*, so statutory day-counts (the s588FL 20-business-day limb,
 * lodgement deadlines, notice periods) need the right state or territory
 * calendar, not an approximation.
 *
 * Design:
 * - INVARIANT: every holiday the engine emits is certain — a legislated
 *   rule or a curated proclamation. It never guesses, so a count can only
 *   ever OVER-state a business-day gap (a missed holiday inflates it), and
 *   a gap at or under a statutory threshold is safe to act on with any
 *   calendar; over-threshold results on an approximate calendar carry the
 *   marginal-band treatment instead.
 * - Only WEEKDAY holidays are modelled. A holiday that falls on a weekend
 *   never changes a business-day count; what matters is the substitute
 *   weekday, which is modelled per jurisdiction.
 * - Deterministic holidays (fixed dates, nth-weekday rules, Easter offsets)
 *   are computed from per-jurisdiction rules.
 * - Proclamation-based holidays (WA's King's Birthday, Victoria's Friday
 *   before the AFL Grand Final) and one-offs (e.g. the 2022 National Day of
 *   Mourning) come from the OVERRIDES table, because they are gazetted year
 *   by year and cannot be derived.
 * - Rule-derived holidays are computable for ANY future year — six of the
 *   eight jurisdictions have no proclamation-based days at all, so their
 *   calendars are exact indefinitely (subject to legislation changes and
 *   unforeseeable one-off proclamations, a documented residual risk).
 *   Uncertainty is scoped to exactly the two non-derivable holidays:
 *   `rangeConfidence` reports "approximate" only when a span touches the
 *   September–October proclamation window of a VIC or WA year whose
 *   proclaimed date has not been curated yet (or predates the rules floor,
 *   or has no jurisdiction at all). Callers making statutory judgments MUST
 *   degrade to a flag-for-manual-check posture when approximate.
 *
 * SOURCES: rules and overrides audited against the official data.gov.au
 * "Australian Public Holidays Dates Machine Readable Dataset" (2021–2025,
 * now inactive) on 2026-08-05, plus wa.gov.au (WA 2024–2026 King's
 * Birthday) and the published VIC 2026 Grand Final Friday. The 2022
 * National Day of Mourning is real but absent from the dataset (published
 * pre-proclamation).
 *
 * DELIBERATE EXCLUSIONS (all err toward OVER-counting business days, which
 * produces a false flag for human review, never a false screen-out):
 * - Regional-only days (NT Show Days incl. Darwin, Brisbane-only Royal
 *   Queensland Show) — not statewide, and the platform models place at
 *   state level.
 * - TAS Easter Tuesday — public-service only.
 * - SA/NT part-day holidays (Christmas Eve / New Year's Eve evenings).
 * NSW's Bank Holiday (1st Mon Aug) IS included: the Corporations Act s9
 * "business day" definition excludes bank holidays in the place concerned.
 *
 * MAINTENANCE: one small annual task — when VIC's Grand Final Friday and
 * WA's King's Birthday are proclaimed for the next year (mid-year), add
 * them to OVERRIDES. Nothing else ages: every other holiday is computed
 * from rules for any year. Until the proclamations land, only VIC/WA spans
 * through September–October degrade to "approximate".
 */

import { parseIsoDate } from "@cvx/keyDates/derivations";

export type AuJurisdiction =
  "ACT" | "NSW" | "NT" | "QLD" | "SA" | "TAS" | "VIC" | "WA";

export const AU_JURISDICTIONS: ReadonlyArray<AuJurisdiction> = [
  "ACT",
  "NSW",
  "NT",
  "QLD",
  "SA",
  "TAS",
  "VIC",
  "WA",
];

/**
 * Rules floor. Structural history back to 2010 is modelled: QLD's pre-2016
 * Labour Day / King's Birthday shuffles, the ACT's Family & Community Day
 * (2010–2017) and its 2018 Anzac substitute change, VIC's Grand Final
 * Friday from 2015, and WA's proclaimed King's/Queen's Birthday are all
 * carried as PROCLAIMED entries (estimated where no primary source has been
 * checked — confirm in Settings → Public Holidays). Years before this
 * report "approximate".
 */
export const RULES_VALID_FROM = 2010;

/**
 * The only non-derivable holidays in the country sit in a September–October
 * proclamation window: WA's King's Birthday (proclaimed yearly; usually but
 * not always the last Monday of September — 2024 was the 23rd) and VIC's
 * Friday before the AFL Grand Final (fixture-dependent; 2020 landed in
 * October). A span touching this window in a year without a curated
 * override is the ONLY rule-based case that reports "approximate".
 */
const PROCLAMATION_WINDOW: Partial<
  Record<AuJurisdiction, { fromMonth: number; toMonth: number }>
> = {
  VIC: { fromMonth: 9, toMonth: 10 },
  WA: { fromMonth: 9, toMonth: 10 },
};

/** Anonymous Gregorian algorithm — Easter Sunday for a year (UTC). */
export function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

// ---------------------------------------------------------------------------
// Date helpers (UTC throughout; ISO YYYY-MM-DD in and out)
// ---------------------------------------------------------------------------

function iso(year: number, month: number, day: number): string {
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

function dayOfWeek(year: number, month: number, day: number): number {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** ISO date of the nth (1-based) given weekday (0=Sun..6=Sat) of a month. */
function nthWeekday(
  year: number,
  month: number,
  weekday: number,
  nth: number,
): string {
  const first = dayOfWeek(year, month, 1);
  const offset = (weekday - first + 7) % 7;
  return iso(year, month, 1 + offset + (nth - 1) * 7);
}

/** First given weekday on or after an anchor date. */
function weekdayOnOrAfter(
  year: number,
  month: number,
  day: number,
  weekday: number,
): string {
  const anchor = dayOfWeek(year, month, day);
  const offset = (weekday - anchor + 7) % 7;
  const d = new Date(Date.UTC(year, month - 1, day + offset));
  return iso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

function shiftDays(
  year: number,
  month: number,
  day: number,
  delta: number,
): string {
  const d = new Date(Date.UTC(year, month - 1, day + delta));
  return iso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/**
 * The weekday holiday produced by a fixed-date holiday under a
 * substitute-Monday regime: the date itself when it is a weekday, else the
 * following Monday (or Tuesday when the Monday is taken by the paired
 * holiday — handled by the caller adding both Christmas and Boxing Day).
 */
function substituteWeekday(
  year: number,
  month: number,
  day: number,
  taken: Set<string>,
): string | null {
  const dow = dayOfWeek(year, month, day);
  let candidate: string;
  if (dow === 6) {
    candidate = shiftDays(year, month, day, 2); // Sat → Mon
  } else if (dow === 0) {
    candidate = shiftDays(year, month, day, 1); // Sun → Mon
  } else {
    candidate = iso(year, month, day);
  }
  while (taken.has(candidate)) {
    const d = parseIsoDate(candidate);
    if (!d) return null;
    candidate = iso(
      d.getUTCFullYear(),
      d.getUTCMonth() + 1,
      d.getUTCDate() + 1,
    );
  }
  return candidate;
}

// ---------------------------------------------------------------------------
// Proclamation-based and one-off holidays (gazetted year by year)
// ---------------------------------------------------------------------------

export type ProclamationStatus = "confirmed" | "estimated";

/**
 * A proclaimed (non-rule-derivable) holiday: VIC's Grand Final Friday, WA's
 * King's Birthday, and one-off national days. `estimated` entries follow the
 * usual pattern (last Friday / last Monday of September) so future counts
 * stay accurate, but they widen the marginal band until confirmed against
 * the gazette — see `estimatedProclaimedBetween` and the classifier.
 * Confirmed entries are authoritative.
 *
 * The admin UI (Settings → Public Holidays) overlays rows from the
 * `publicHolidayAdjustments` table via {@link mergeProclaimed}, so the
 * annual gazette check is a UI task, not a code change.
 */
export interface ProclaimedHoliday {
  year: number;
  /** "ALL" applies to every jurisdiction (one-off national proclamations). */
  jurisdiction: AuJurisdiction | "ALL";
  date: string;
  label: string;
  status: ProclamationStatus;
}

const GF = "Grand Final Friday";
const WAKB = "King's Birthday (WA)";

export const PROCLAIMED_BASELINE: ProclaimedHoliday[] = [
  // --- Confirmed (audited against data.gov.au 2021-2025, wa.gov.au, and
  //     published state dates) ------------------------------------------
  {
    year: 2018,
    jurisdiction: "VIC",
    date: "2018-09-28",
    label: GF,
    status: "confirmed",
  },
  {
    year: 2019,
    jurisdiction: "VIC",
    date: "2019-09-27",
    label: GF,
    status: "confirmed",
  },
  {
    year: 2020,
    jurisdiction: "VIC",
    date: "2020-10-23",
    label: GF,
    status: "confirmed",
  }, // COVID-delayed
  {
    year: 2021,
    jurisdiction: "VIC",
    date: "2021-09-24",
    label: GF,
    status: "confirmed",
  },
  {
    year: 2022,
    jurisdiction: "VIC",
    date: "2022-09-23",
    label: GF,
    status: "confirmed",
  },
  {
    year: 2023,
    jurisdiction: "VIC",
    date: "2023-09-29",
    label: GF,
    status: "confirmed",
  },
  {
    year: 2024,
    jurisdiction: "VIC",
    date: "2024-09-27",
    label: GF,
    status: "confirmed",
  },
  {
    year: 2025,
    jurisdiction: "VIC",
    date: "2025-09-26",
    label: GF,
    status: "confirmed",
  },
  {
    year: 2026,
    jurisdiction: "VIC",
    date: "2026-09-25",
    label: GF,
    status: "confirmed",
  },
  {
    year: 2021,
    jurisdiction: "WA",
    date: "2021-09-27",
    label: WAKB,
    status: "confirmed",
  },
  {
    year: 2022,
    jurisdiction: "WA",
    date: "2022-09-26",
    label: WAKB,
    status: "confirmed",
  },
  {
    year: 2023,
    jurisdiction: "WA",
    date: "2023-09-25",
    label: WAKB,
    status: "confirmed",
  },
  {
    year: 2024,
    jurisdiction: "WA",
    date: "2024-09-23",
    label: WAKB,
    status: "confirmed",
  }, // broke last-Monday pattern
  {
    year: 2025,
    jurisdiction: "WA",
    date: "2025-09-29",
    label: WAKB,
    status: "confirmed",
  },
  {
    year: 2026,
    jurisdiction: "WA",
    date: "2026-09-28",
    label: WAKB,
    status: "confirmed",
  },
  {
    year: 2022,
    jurisdiction: "ALL",
    date: "2022-09-22",
    label: "National Day of Mourning",
    status: "confirmed",
  },
  // --- Historic WA King's (Queen's) Birthday — outside the data.gov.au
  //     dataset's coverage; verified against officeholidays.com historical
  //     year pages (secondary source), each the last Monday of September ---
  {
    year: 2018,
    jurisdiction: "WA",
    date: "2018-09-24",
    label: WAKB,
    status: "confirmed",
  },
  {
    year: 2019,
    jurisdiction: "WA",
    date: "2019-09-30",
    label: WAKB,
    status: "confirmed",
  },
  {
    year: 2020,
    jurisdiction: "WA",
    date: "2020-09-28",
    label: WAKB,
    status: "confirmed",
  },
  // --- Historic proclaimed / non-rule dates, 2010–2017 (estimated from
  //     historical records; confirm in the admin UI if a primary source is
  //     ever needed for a matter touching these years) -------------------
  {
    year: 2010,
    jurisdiction: "WA",
    date: "2010-09-27",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2011,
    jurisdiction: "WA",
    date: "2011-10-28",
    label: WAKB,
    status: "estimated",
  }, // CHOGM move
  {
    year: 2012,
    jurisdiction: "WA",
    date: "2012-10-01",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2013,
    jurisdiction: "WA",
    date: "2013-09-30",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2014,
    jurisdiction: "WA",
    date: "2014-09-29",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2015,
    jurisdiction: "WA",
    date: "2015-09-28",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2016,
    jurisdiction: "WA",
    date: "2016-09-26",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2017,
    jurisdiction: "WA",
    date: "2017-09-25",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2015,
    jurisdiction: "VIC",
    date: "2015-10-02",
    label: GF,
    status: "estimated",
  },
  {
    year: 2016,
    jurisdiction: "VIC",
    date: "2016-09-30",
    label: GF,
    status: "estimated",
  },
  {
    year: 2017,
    jurisdiction: "VIC",
    date: "2017-09-29",
    label: GF,
    status: "estimated",
  },
  {
    year: 2010,
    jurisdiction: "QLD",
    date: "2010-05-03",
    label: "Labour Day (QLD)",
    status: "estimated",
  },
  {
    year: 2011,
    jurisdiction: "QLD",
    date: "2011-05-02",
    label: "Labour Day (QLD)",
    status: "estimated",
  },
  {
    year: 2012,
    jurisdiction: "QLD",
    date: "2012-05-07",
    label: "Labour Day (QLD)",
    status: "estimated",
  },
  {
    year: 2013,
    jurisdiction: "QLD",
    date: "2013-10-07",
    label: "Labour Day (QLD)",
    status: "estimated",
  },
  {
    year: 2014,
    jurisdiction: "QLD",
    date: "2014-10-06",
    label: "Labour Day (QLD)",
    status: "estimated",
  },
  {
    year: 2015,
    jurisdiction: "QLD",
    date: "2015-10-05",
    label: "Labour Day (QLD)",
    status: "estimated",
  },
  {
    year: 2010,
    jurisdiction: "QLD",
    date: "2010-06-14",
    label: "Queen's Birthday (QLD)",
    status: "estimated",
  },
  {
    year: 2011,
    jurisdiction: "QLD",
    date: "2011-06-13",
    label: "Queen's Birthday (QLD)",
    status: "estimated",
  },
  {
    year: 2012,
    jurisdiction: "QLD",
    date: "2012-06-11",
    label: "Queen's Birthday (QLD)",
    status: "estimated",
  },
  {
    year: 2012,
    jurisdiction: "QLD",
    date: "2012-10-01",
    label: "Queen's Diamond Jubilee (QLD)",
    status: "estimated",
  },
  {
    year: 2013,
    jurisdiction: "QLD",
    date: "2013-06-10",
    label: "Queen's Birthday (QLD)",
    status: "estimated",
  },
  {
    year: 2014,
    jurisdiction: "QLD",
    date: "2014-06-09",
    label: "Queen's Birthday (QLD)",
    status: "estimated",
  },
  {
    year: 2015,
    jurisdiction: "QLD",
    date: "2015-06-08",
    label: "Queen's Birthday (QLD)",
    status: "estimated",
  },
  {
    year: 2010,
    jurisdiction: "ACT",
    date: "2010-09-27",
    label: "Family & Community Day (ACT)",
    status: "estimated",
  },
  {
    year: 2011,
    jurisdiction: "ACT",
    date: "2011-10-10",
    label: "Family & Community Day (ACT)",
    status: "estimated",
  },
  {
    year: 2012,
    jurisdiction: "ACT",
    date: "2012-10-08",
    label: "Family & Community Day (ACT)",
    status: "estimated",
  },
  {
    year: 2013,
    jurisdiction: "ACT",
    date: "2013-09-30",
    label: "Family & Community Day (ACT)",
    status: "estimated",
  },
  {
    year: 2014,
    jurisdiction: "ACT",
    date: "2014-09-29",
    label: "Family & Community Day (ACT)",
    status: "estimated",
  },
  {
    year: 2015,
    jurisdiction: "ACT",
    date: "2015-09-28",
    label: "Family & Community Day (ACT)",
    status: "estimated",
  },
  {
    year: 2016,
    jurisdiction: "ACT",
    date: "2016-09-26",
    label: "Family & Community Day (ACT)",
    status: "estimated",
  },
  {
    year: 2017,
    jurisdiction: "ACT",
    date: "2017-09-25",
    label: "Family & Community Day (ACT)",
    status: "estimated",
  },
  // --- Decade estimates: confirm each year from the gazette in the admin
  //     UI (VIC: last Friday of September; WA: last Monday of September) --
  {
    year: 2027,
    jurisdiction: "VIC",
    date: "2027-09-24",
    label: GF,
    status: "estimated",
  },
  {
    year: 2028,
    jurisdiction: "VIC",
    date: "2028-09-29",
    label: GF,
    status: "estimated",
  },
  {
    year: 2029,
    jurisdiction: "VIC",
    date: "2029-09-28",
    label: GF,
    status: "estimated",
  },
  {
    year: 2030,
    jurisdiction: "VIC",
    date: "2030-09-27",
    label: GF,
    status: "estimated",
  },
  {
    year: 2031,
    jurisdiction: "VIC",
    date: "2031-09-26",
    label: GF,
    status: "estimated",
  },
  {
    year: 2032,
    jurisdiction: "VIC",
    date: "2032-09-24",
    label: GF,
    status: "estimated",
  },
  {
    year: 2033,
    jurisdiction: "VIC",
    date: "2033-09-30",
    label: GF,
    status: "estimated",
  },
  {
    year: 2034,
    jurisdiction: "VIC",
    date: "2034-09-29",
    label: GF,
    status: "estimated",
  },
  {
    year: 2035,
    jurisdiction: "VIC",
    date: "2035-09-28",
    label: GF,
    status: "estimated",
  },
  {
    year: 2036,
    jurisdiction: "VIC",
    date: "2036-09-26",
    label: GF,
    status: "estimated",
  },
  {
    year: 2027,
    jurisdiction: "WA",
    date: "2027-09-27",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2028,
    jurisdiction: "WA",
    date: "2028-09-25",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2029,
    jurisdiction: "WA",
    date: "2029-09-24",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2030,
    jurisdiction: "WA",
    date: "2030-09-30",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2031,
    jurisdiction: "WA",
    date: "2031-09-29",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2032,
    jurisdiction: "WA",
    date: "2032-09-27",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2033,
    jurisdiction: "WA",
    date: "2033-09-26",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2034,
    jurisdiction: "WA",
    date: "2034-09-25",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2035,
    jurisdiction: "WA",
    date: "2035-09-24",
    label: WAKB,
    status: "estimated",
  },
  {
    year: 2036,
    jurisdiction: "WA",
    date: "2036-09-29",
    label: WAKB,
    status: "estimated",
  },
];

/** Key identifying one proclaimed slot (a jurisdiction's holiday in a year). */
function proclaimedKey(e: {
  year: number;
  jurisdiction: string;
  label: string;
}): string {
  return `${e.year}:${e.jurisdiction}:${e.label}`;
}

/**
 * Overlays admin adjustments (from the `publicHolidayAdjustments` table)
 * onto the baseline: an adjustment replaces the baseline entry for the same
 * (year, jurisdiction, label) and may add new one-off entries.
 */
export function mergeProclaimed(
  adjustments: ProclaimedHoliday[],
): ProclaimedHoliday[] {
  const byKey = new Map<string, ProclaimedHoliday>();
  for (const e of PROCLAIMED_BASELINE) byKey.set(proclaimedKey(e), e);
  for (const e of adjustments) byKey.set(proclaimedKey(e), e);
  return [...byKey.values()];
}

// ---------------------------------------------------------------------------
// The calendar
// ---------------------------------------------------------------------------

/** Anzac Day weekend-substitute regimes (they genuinely differ by state). */
const ANZAC_SUBSTITUTE: Record<
  AuJurisdiction,
  "none" | "sunday_only" | "weekend"
> = {
  ACT: "weekend",
  NSW: "none",
  NT: "sunday_only",
  QLD: "sunday_only",
  SA: "sunday_only",
  TAS: "none",
  VIC: "none",
  WA: "weekend",
};

export interface HolidayDetail {
  date: string;
  name: string;
  /** Practitioner-facing derivation rule for the date. */
  rule: string;
  source: "rule" | "substitute" | "confirmed" | "estimated";
}

const detailsCache = new Map<string, HolidayDetail[]>();

/**
 * Every WEEKDAY public holiday for a year in a jurisdiction, with its name
 * and the rule that produced the date. This is the single source of truth —
 * {@link weekdayHolidays} derives its date Set from it, so the Settings UI
 * and the business-day arithmetic can never disagree.
 */
export function holidayDetails(
  year: number,
  jurisdiction?: AuJurisdiction,
  proclaimed: ProclaimedHoliday[] = PROCLAIMED_BASELINE,
): HolidayDetail[] {
  const cacheable = proclaimed === PROCLAIMED_BASELINE;
  const cacheKey = `${year}:${jurisdiction ?? "NATIONAL"}`;
  if (cacheable) {
    const cached = detailsCache.get(cacheKey);
    if (cached) return cached;
  }

  const details: HolidayDetail[] = [];
  const seen = new Set<string>();
  const push = (
    date: string | null,
    name: string,
    rule: string,
    source: HolidayDetail["source"] = "rule",
  ) => {
    if (!date) return;
    const d = parseIsoDate(date);
    if (!d) return;
    const dow = d.getUTCDay();
    if (dow === 0 || dow === 6) return;
    if (seen.has(date)) return;
    seen.add(date);
    details.push({ date, name, rule, source });
  };
  /** Fixed-date holiday under a substitute-weekday regime. */
  const pushFixed = (
    month: number,
    day: number,
    name: string,
    rule: string,
  ) => {
    const actual = iso(year, month, day);
    const sub = substituteWeekday(year, month, day, seen);
    if (sub === actual) push(actual, name, rule);
    else
      push(
        sub,
        `${name} (substitute)`,
        `${rule}; observed on the following weekday when it falls on a weekend`,
        "substitute",
      );
  };
  const MON = 1;

  // --- Nationwide ---------------------------------------------------------
  pushFixed(1, 1, "New Year's Day", "1 January");
  pushFixed(1, 26, "Australia Day", "26 January");
  const easter = easterSunday(year);
  push(
    shiftDays(year, easter.month, easter.day, -2),
    "Good Friday",
    "Two days before Easter Sunday",
  );
  push(
    shiftDays(year, easter.month, easter.day, 1),
    "Easter Monday",
    "The day after Easter Sunday",
  );
  pushFixed(12, 25, "Christmas Day", "25 December");
  pushFixed(
    12,
    26,
    jurisdiction === "SA" ? "Proclamation Day" : "Boxing Day",
    "26 December",
  );
  // Anzac Day: national on its own day; weekend substitutes are per-state.
  const anzacDow = dayOfWeek(year, 4, 25);
  if (anzacDow !== 0 && anzacDow !== 6) {
    push(iso(year, 4, 25), "Anzac Day", "25 April");
  }

  if (jurisdiction) {
    // The ACT's weekend substitute for Anzac Day dates from its 2018
    // Holidays Act change; before that only a Sunday was substituted.
    const regime =
      jurisdiction === "ACT" && year < 2018
        ? "sunday_only"
        : ANZAC_SUBSTITUTE[jurisdiction];
    if (
      (regime === "weekend" && (anzacDow === 0 || anzacDow === 6)) ||
      (regime === "sunday_only" && anzacDow === 0)
    ) {
      push(
        substituteWeekday(year, 4, 25, seen),
        "Anzac Day (substitute)",
        regime === "weekend"
          ? "25 April; this state observes the following Monday when it falls on a weekend"
          : "25 April; this state observes the following Monday when it falls on a Sunday",
        "substitute",
      );
    }

    // --- State / territory days (weekday-affecting, statewide) ------------
    switch (jurisdiction) {
      case "ACT":
        push(
          nthWeekday(year, 3, MON, 2),
          "Canberra Day",
          "Second Monday of March",
        );
        if (year >= 2018) {
          push(
            weekdayOnOrAfter(year, 5, 27, MON),
            "Reconciliation Day",
            "The Monday on or after 27 May",
          );
        }
        push(
          nthWeekday(year, 6, MON, 2),
          "King's Birthday",
          "Second Monday of June",
        );
        push(
          nthWeekday(year, 10, MON, 1),
          "Labour Day",
          "First Monday of October",
        );
        break;
      case "NSW":
        push(
          nthWeekday(year, 6, MON, 2),
          "King's Birthday",
          "Second Monday of June",
        );
        push(
          nthWeekday(year, 8, MON, 1),
          "Bank Holiday",
          "First Monday of August — banks only, but the Corporations Act s9 business-day definition excludes bank holidays",
        );
        push(
          nthWeekday(year, 10, MON, 1),
          "Labour Day",
          "First Monday of October",
        );
        break;
      case "NT":
        push(nthWeekday(year, 5, MON, 1), "May Day", "First Monday of May");
        push(
          nthWeekday(year, 6, MON, 2),
          "King's Birthday",
          "Second Monday of June",
        );
        push(
          nthWeekday(year, 8, MON, 1),
          "Picnic Day",
          "First Monday of August",
        );
        break;
      case "QLD":
        // Pre-2016 QLD moved both days around (Labour Day sat in October
        // 2013–2015; the King's Birthday moved to October from 2016) — those
        // years come from the PROCLAIMED entries below.
        if (year >= 2016) {
          push(
            nthWeekday(year, 5, MON, 1),
            "Labour Day",
            "First Monday of May",
          );
          push(
            nthWeekday(year, 10, MON, 1),
            "King's Birthday",
            "First Monday of October (since 2016)",
          );
        }
        break;
      case "SA":
        push(
          nthWeekday(year, 3, MON, 2),
          "Adelaide Cup Day",
          "Second Monday of March",
        );
        push(
          nthWeekday(year, 6, MON, 2),
          "King's Birthday",
          "Second Monday of June",
        );
        push(
          nthWeekday(year, 10, MON, 1),
          "Labour Day",
          "First Monday of October",
        );
        break;
      case "TAS":
        push(
          nthWeekday(year, 3, MON, 2),
          "Eight Hours Day",
          "Second Monday of March",
        );
        push(
          nthWeekday(year, 6, MON, 2),
          "King's Birthday",
          "Second Monday of June",
        );
        break;
      case "VIC":
        push(
          nthWeekday(year, 3, MON, 2),
          "Labour Day",
          "Second Monday of March",
        );
        push(
          nthWeekday(year, 6, MON, 2),
          "King's Birthday",
          "Second Monday of June",
        );
        // weekday 2 = Tuesday
        push(
          nthWeekday(year, 11, 2, 1),
          "Melbourne Cup Day",
          "First Tuesday of November",
        );
        break;
      case "WA":
        push(
          nthWeekday(year, 3, MON, 1),
          "Labour Day",
          "First Monday of March",
        );
        push(
          nthWeekday(year, 6, MON, 1),
          "Western Australia Day",
          "First Monday of June",
        );
        // King's Birthday comes from the PROCLAIMED entries below.
        break;
    }
  }
  // --- Proclaimed holidays (confirmed and estimated) ----------------------
  for (const e of proclaimed) {
    if (e.year !== year) continue;
    if (e.jurisdiction === "ALL" || e.jurisdiction === jurisdiction) {
      push(
        e.date,
        e.label,
        e.status === "confirmed"
          ? "Proclaimed for this year (gazetted date, confirmed)"
          : "Estimated from the usual pattern — confirm from the gazette in Settings once proclaimed",
        e.status,
      );
    }
  }

  details.sort((a, b) => a.date.localeCompare(b.date));
  if (cacheable) detailsCache.set(cacheKey, details);
  return details;
}

/**
 * The WEEKDAY public holidays for a year in a jurisdiction (or the nationwide
 * intersection when no jurisdiction is given), as a Set of ISO dates.
 * `proclaimed` defaults to the baseline; pass `mergeProclaimed(adjustments)`
 * to overlay admin-confirmed dates.
 */
export function weekdayHolidays(
  year: number,
  jurisdiction?: AuJurisdiction,
  proclaimed: ProclaimedHoliday[] = PROCLAIMED_BASELINE,
): Set<string> {
  return new Set(
    holidayDetails(year, jurisdiction, proclaimed).map((d) => d.date),
  );
}

/**
 * ESTIMATED proclaimed weekday holidays falling strictly after `fromIso` up
 * to and including `toIso` — the count of uncertain days in a span. A
 * boundary result within this distance of a statutory threshold must not be
 * concluded (the real gazetted date could move each one).
 */
export function estimatedProclaimedBetween(
  fromIso: string,
  toIso: string,
  jurisdiction?: AuJurisdiction,
  proclaimed: ProclaimedHoliday[] = PROCLAIMED_BASELINE,
): number {
  return proclaimed.filter(
    (e) =>
      e.status === "estimated" &&
      (e.jurisdiction === "ALL" || e.jurisdiction === jurisdiction) &&
      e.date > fromIso &&
      e.date <= toIso,
  ).length;
}

export type CalendarConfidence = "exact" | "approximate";

/**
 * Year-level confidence: "approximate" when no jurisdiction is known, the
 * year predates the rules floor, or the jurisdiction's proclamation-based
 * holiday (VIC / WA) is not curated for that year. Rule-only jurisdictions
 * are exact for ANY future year. Prefer {@link rangeConfidence} for a date
 * span — it scopes the VIC/WA uncertainty to the actual proclamation
 * window instead of the whole year.
 */
export function calendarConfidence(
  year: number,
  jurisdiction?: AuJurisdiction,
  proclaimed: ProclaimedHoliday[] = PROCLAIMED_BASELINE,
): CalendarConfidence {
  if (!jurisdiction) return "approximate";
  if (year < RULES_VALID_FROM) return "approximate";
  if (PROCLAMATION_WINDOW[jurisdiction]) {
    const entry = proclaimed.find(
      (e) =>
        e.year === year &&
        e.jurisdiction === jurisdiction &&
        e.status === "confirmed",
    );
    if (!entry) return "approximate";
  }
  return "exact";
}

/**
 * Confidence for a date span. "approximate" only when the span has no
 * jurisdiction, starts before the rules floor, contains an ESTIMATED
 * proclaimed date, or touches the September–October proclamation window of
 * a VIC / WA year with no confirmed date — every other span is exact,
 * indefinitely.
 */
export function rangeConfidence(
  fromIso: string,
  toIso: string,
  jurisdiction?: AuJurisdiction,
  proclaimed: ProclaimedHoliday[] = PROCLAIMED_BASELINE,
): CalendarConfidence {
  const from = parseIsoDate(fromIso);
  const to = parseIsoDate(toIso);
  if (!from || !to || !jurisdiction) return "approximate";
  if (from.getUTCFullYear() < RULES_VALID_FROM) return "approximate";
  if (
    estimatedProclaimedBetween(fromIso, toIso, jurisdiction, proclaimed) > 0
  ) {
    return "approximate";
  }

  const window = PROCLAMATION_WINDOW[jurisdiction];
  if (!window) return "exact";
  for (let y = from.getUTCFullYear(); y <= to.getUTCFullYear(); y++) {
    const confirmed = proclaimed.some(
      (e) =>
        e.year === y &&
        e.jurisdiction === jurisdiction &&
        e.status === "confirmed",
    );
    if (confirmed) continue;
    const hasEstimate = proclaimed.some(
      (e) => e.year === y && e.jurisdiction === jurisdiction,
    );
    // An estimated entry inside the span is already caught above; a year
    // with NO entry at all (beyond the estimate horizon) degrades any span
    // touching its proclamation window.
    if (hasEstimate) continue;
    const windowStart = Date.UTC(y, window.fromMonth - 1, 1);
    const windowEnd = Date.UTC(y, window.toMonth, 0, 23, 59, 59);
    if (from.getTime() <= windowEnd && to.getTime() >= windowStart) {
      return "approximate";
    }
  }
  return "exact";
}

/** Normalise a free-text state value ("NSW", "nsw", "New South Wales") if possible. */
export function parseJurisdiction(
  raw: string | undefined,
): AuJurisdiction | undefined {
  if (!raw) return undefined;
  const upper = raw.trim().toUpperCase();
  if ((AU_JURISDICTIONS as readonly string[]).includes(upper)) {
    return upper as AuJurisdiction;
  }
  const byName: Record<string, AuJurisdiction> = {
    "AUSTRALIAN CAPITAL TERRITORY": "ACT",
    "NEW SOUTH WALES": "NSW",
    "NORTHERN TERRITORY": "NT",
    QUEENSLAND: "QLD",
    "SOUTH AUSTRALIA": "SA",
    TASMANIA: "TAS",
    VICTORIA: "VIC",
    "WESTERN AUSTRALIA": "WA",
  };
  return byName[upper];
}

// ---------------------------------------------------------------------------
// Business-day arithmetic
// ---------------------------------------------------------------------------

export function isPublicHoliday(
  isoDate: string,
  jurisdiction?: AuJurisdiction,
  proclaimed: ProclaimedHoliday[] = PROCLAIMED_BASELINE,
): boolean {
  const d = parseIsoDate(isoDate);
  if (!d) return false;
  return weekdayHolidays(d.getUTCFullYear(), jurisdiction, proclaimed).has(
    isoDate,
  );
}

export function isBusinessDay(
  isoDate: string,
  jurisdiction?: AuJurisdiction,
  proclaimed: ProclaimedHoliday[] = PROCLAIMED_BASELINE,
): boolean {
  const d = parseIsoDate(isoDate);
  if (!d) return false;
  const dow = d.getUTCDay();
  if (dow === 0 || dow === 6) return false;
  return !isPublicHoliday(isoDate, jurisdiction, proclaimed);
}

/**
 * Business days strictly after `fromIso` up to and including `toIso`.
 * Null when either date is unparseable or the range is inverted.
 */
export function businessDaysBetween(
  fromIso: string,
  toIso: string,
  jurisdiction?: AuJurisdiction,
  proclaimed: ProclaimedHoliday[] = PROCLAIMED_BASELINE,
): number | null {
  const from = parseIsoDate(fromIso);
  const to = parseIsoDate(toIso);
  if (!from || !to || to.getTime() < from.getTime()) return null;
  let count = 0;
  const cursor = new Date(from.getTime());
  while (cursor.getTime() < to.getTime()) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const cursorIso = iso(
      cursor.getUTCFullYear(),
      cursor.getUTCMonth() + 1,
      cursor.getUTCDate(),
    );
    if (isBusinessDay(cursorIso, jurisdiction, proclaimed)) count += 1;
  }
  return count;
}

/** The date `n` business days after `fromIso` (n ≥ 0; n=0 returns fromIso). */
export function addBusinessDays(
  fromIso: string,
  n: number,
  jurisdiction?: AuJurisdiction,
  proclaimed: ProclaimedHoliday[] = PROCLAIMED_BASELINE,
): string | null {
  const from = parseIsoDate(fromIso);
  if (!from || n < 0 || !Number.isInteger(n)) return null;
  const cursor = new Date(from.getTime());
  let remaining = n;
  while (remaining > 0) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const cursorIso = iso(
      cursor.getUTCFullYear(),
      cursor.getUTCMonth() + 1,
      cursor.getUTCDate(),
    );
    if (isBusinessDay(cursorIso, jurisdiction, proclaimed)) remaining -= 1;
  }
  return iso(
    cursor.getUTCFullYear(),
    cursor.getUTCMonth() + 1,
    cursor.getUTCDate(),
  );
}
